import { v4 as uuid } from 'uuid';

import { trackOpsEvent } from 'apis/amethyst';
import marketplace from 'cfg/marketplace.json';

type OpsStatus = 'failure' | 'success';

export const GLOBAL_COMPONENTS_SOURCE_PAGE = 'global';

type AccountOperation = 'AccountManagement';
type OrderOperation = 'GuestOrderDataLoad' | 'OrderCancel' | 'OrderDataLoad' | 'OrderHistoryDataLoad';
type ReturnOperation = 'ReturnFormDataLoad' | 'ReturnSubmit';
type ReviewsOperation = 'ShareOutfitUploadPhoto' | 'WriteReview';
type TrackingOperation = 'PackageTrackingDataLoad';
type PlpOperation = 'SearchResultsLoad' | 'FilterSortApplication' | 'SponsoredResultsLoad' | 'SaveToFavorites';
type RecommendationsOperation = 'GetRecommendations' | 'GetFooterRecommendations';
type FooterSlotOperation = 'FooterLegalLinksSlotDataLoad' | 'FooterSlotDataLoad';
type GlobalComponentName = 'ViewCart' | 'ChangeCartItemQuantity' | 'SearchAutoComplete' | 'EmailSignup' | FooterSlotOperation;

type ConsentOperation = 'ConsentPreferencesLoad' | 'ConsentPreferencesUpdate';
type FavoritesOperation = 'FavoritesDataLoad' | 'FavoritesListAction' | 'FavoritesModalRender' | 'SaveToFavoritesInteraction';

type SubscriptionOperation = 'SubscriptionDataLoad' | 'SubscriptionSaveChanges' | 'SubscriptionUnsubscribeAll';
type FederatedLoginOperation = 'FederatedLoginRender';
type UtilityPagesOperation = ConsentOperation | FavoritesOperation | FederatedLoginOperation | SubscriptionOperation;

export type UserOperation =
  | AccountOperation
  | GlobalComponentName
  | OrderOperation
  | PlpOperation
  | RecommendationsOperation
  | ReturnOperation
  | ReviewsOperation
  | TrackingOperation
  | UtilityPagesOperation;

type PlpAttiExperience = 'SearchSubmitToResults' | 'FilterSortApply';
type CartAttiExperience = 'ViewCart';

// Extend this union as new pages add ATTI metrics
export type AttiExperience = CartAttiExperience | PlpAttiExperience;

export const OPS_SESSION_KEYS = {
  componentRenderError: (pageType: string) => `componentRenderError_${pageType}`,
  uncaughtException: (pageType: string) => `uncaughtException_${pageType}`
} as const;

type OpsSessionKey = ReturnType<(typeof OPS_SESSION_KEYS)[keyof typeof OPS_SESSION_KEYS]>;

function getBaseOpsEvent() {
  return {
    clientDatetimeUtc: Date.now().toString(),
    eventId: uuid().replace(/-/g, ''),
    marketplace: marketplace.domain,
    timestamp: Date.now(),
    urlPath: typeof window !== 'undefined' ? window.location?.pathname : ''
  };
}

/**
 * Returns true if this is the first time this key has been seen in the session.
 * Uses sessionStorage for deduplication — returns true (allow) if unavailable.
 */
function shouldEmitOncePerSession(sessionKey: OpsSessionKey): boolean {
  try {
    if (sessionStorage.getItem(sessionKey)) {
      return false;
    }
    sessionStorage.setItem(sessionKey, '1');
  } catch {
    // sessionStorage unavailable — allow emit
  }
  return true;
}

function isGlobalComponentName(operation: UserOperation): operation is GlobalComponentName {
  return (
    operation === 'ViewCart' ||
    operation === 'ChangeCartItemQuantity' ||
    operation === 'SearchAutoComplete' ||
    operation === 'EmailSignup' ||
    operation === 'FooterLegalLinksSlotDataLoad' ||
    operation === 'FooterSlotDataLoad'
  );
}

/**
 * Fires a USER_OPERATION ops event via the Amethyst pipeline.
 * Global component operations (cart, search, email signup, and footer slot loads) automatically get a '-marty' suffix.
 *
 * Usage:
 *   trackOperation('WriteReview', 'success');
 *   trackOperation('ViewCart', 'success'); // emits 'ViewCart-marty'
 */
export function trackOperation(operation: UserOperation, status: OpsStatus) {
  const resolvedOperation = isGlobalComponentName(operation) ? `${operation}-marty` : operation;
  trackOpsEvent({
    ...getBaseOpsEvent(),
    eventTypeId: 'USER_OPERATION',
    userOperation: { operation: resolvedOperation, status }
  });
}

/**
 * Tracks a failure ops event only if the error status is not 403 (unauthenticated).
 * Auth errors mean the user isn't logged in — the operation never ran,
 * so it shouldn't count as a failure in metrics.
 */
export function trackOperationFailureIfAuthed(operation: UserOperation, statusCode?: number) {
  const UNAUTHED_CODE = 403;
  if (statusCode !== UNAUTHED_CODE) {
    trackOperation(operation, 'failure');
  }
}

/**
 * Fires a USER_ATTI ops event measuring perceived latency from user action
 * to visible result. Uses the new string-based UserATTI proto format.
 *
 * @param category  Overall metric category (e.g. 'Cart', 'Search')
 * @param experience  Specific experience being tracked (e.g. 'SearchSubmitToResults')
 */
export function trackAttiEvent(category: string, experience: AttiExperience, actionTimestamp: number, imagesTimestamp: number, suffix?: string) {
  trackOpsEvent({
    ...getBaseOpsEvent(),
    eventTypeId: 'USER_ATTI',
    userAtti: {
      metric_name: `${category}.${experience}`,
      experience: suffix ? `${experience}-${suffix}` : experience,
      action_timestamp: actionTimestamp,
      images_timestamp: imagesTimestamp
    }
  });
}

/**
 * Fires an ERROR_PAGE ops event when an error page (404/500) or error
 * boundary fallback renders. Used to track per-page error rates.
 */
export function trackErrorPage(sourcePage: string) {
  trackOpsEvent({
    ...getBaseOpsEvent(),
    eventTypeId: 'ERROR_PAGE',
    errorPage: { sourcePage }
  });
}

/**
 * Fires an UNCAUGHT_EXCEPTION ops event when a global error or unhandled
 * promise rejection is caught by the window-level listeners. Deduped per
 * page type per session via sessionStorage to match Kratos behavior.
 */
export function trackUncaughtException(sourcePage: string) {
  if (!shouldEmitOncePerSession(OPS_SESSION_KEYS.uncaughtException(sourcePage))) {
    return;
  }
  trackOpsEvent({
    ...getBaseOpsEvent(),
    eventTypeId: 'UNCAUGHT_EXCEPTION',
    uncaughtException: { sourcePage }
  });
}

/**
 * Fires a PAGE_REQUEST ops event on every page navigation. Serves as the
 * denominator for error page and uncaught exception rate metrics.
 */
export function trackPageRequest(pageType: string) {
  trackOpsEvent({
    ...getBaseOpsEvent(),
    eventTypeId: 'PAGE_REQUEST',
    pageRequest: { pageType }
  });
}

/**
 * Fires a COMPONENT_RENDER_ERROR ops event when a React error boundary catches
 * a component render failure. Deduped per page type per session via sessionStorage.
 */
export function trackComponentRenderError(sourcePage: string, componentName: string) {
  if (!shouldEmitOncePerSession(OPS_SESSION_KEYS.componentRenderError(sourcePage))) {
    return;
  }
  trackOpsEvent({
    ...getBaseOpsEvent(),
    eventTypeId: 'COMPONENT_RENDER_ERROR',
    componentRenderError: { sourcePage, componentName }
  });
}
